import { useEffect, useRef, useState } from 'react';
import { useSimStore } from '../../store/useSimStore';
import HelpTip from './HelpTip';
import styles from './CaseRecorder.module.css';

// ── Helpers ──────────────────────────────────────────────────────────────────
const pad2 = (n) => String(n).padStart(2, '0');
/** ms since case start → M:SS */
function mmss(ms) {
  const s = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(s / 60)}:${pad2(s % 60)}`;
}
function stamp(epoch) {
  const d = new Date(epoch);
  return `${d.getFullYear()}${pad2(d.getMonth() + 1)}${pad2(d.getDate())}_${pad2(d.getHours())}${pad2(d.getMinutes())}${pad2(d.getSeconds())}`;
}

function download(name, text, mime) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const SAMPLE_COLS = ['HR', 'SpO2', 'NIBP_Sys', 'NIBP_Dia', 'ABP_Sys', 'ABP_Dia', 'EtCO2', 'RR', 'Temp', 'CVP', 'BIS', 'TOF'];

// Only the parameters that were actually recorded (their channel was active at
// some point). Keeps inactive channels out of the table / trend / export.
function activeCols(samples) {
  return SAMPLE_COLS.filter((c) => samples.some((s) => s[c] != null));
}

function buildCSV(rec) {
  const cols = activeCols(rec.samples);
  const dur = (rec.endedAt || Date.now()) - rec.startedAt;
  const L = [];
  L.push('Anesthesia Sim — Case Record');
  L.push(`Started,${new Date(rec.startedAt).toISOString()}`);
  L.push(`Ended,${rec.endedAt ? new Date(rec.endedAt).toISOString() : ''}`);
  L.push(`Duration,${mmss(dur)}`);
  L.push('');
  L.push('VITALS');
  L.push(['Time', ...cols].join(','));
  rec.samples.forEach((s) => L.push([mmss(s.t), ...cols.map((k) => (s[k] ?? ''))].join(',')));
  L.push('');
  L.push('EVENTS');
  L.push('Elapsed,Clock,Type,Detail');
  rec.events.forEach((e) => L.push([
    mmss(e.t),
    e.at ? new Date(e.at).toLocaleTimeString([], { hour12: false }) : '',
    e.kind,
    `"${String(e.label).replace(/"/g, '""')}"`,
  ].join(',')));
  return L.join('\n');
}

// Event marker colour by kind
const EVENT_COLOR = {
  scenario: '#FF9100',
  modifier: '#CF9FFF',
  intervention: '#00BCD4',
  procedure: '#FF80AB',
  shock: '#FF1744',
  screen: '#66BB6A',
  media: '#4DD0E1',
  aid: '#FFF176',
};

// Trend lanes (parameter → colour) — matched to the monitor channel colours:
// ECG green, SpO₂ white, NIBP red, EtCO₂ yellow. RR stays neutral (it shares
// the yellow CO₂ tile on the monitor, so a distinct grey keeps the lanes legible).
const LANES = [
  { keys: ['HR'], label: 'HR', color: '#00E676' },
  { keys: ['SpO2'], label: 'SpO₂', color: '#FFFFFF' },
  { keys: ['NIBP_Sys', 'NIBP_Dia'], label: 'NIBP', color: '#FF1744' },
  { keys: ['EtCO2'], label: 'EtCO₂', color: '#FFD600' },
  { keys: ['RR'], label: 'RR', color: '#B0BEC5' },
];

// ── Trend chart (small-multiples canvas) ─────────────────────────────────────
function TrendChart({ rec }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Only lanes whose parameter was actually recorded (channel active at some point).
    const lanes = LANES.filter((l) => l.keys.some((k) => rec.samples.some((s) => s[k] != null)));
    if (lanes.length === 0) return;
    const cssW = canvas.clientWidth || 600;
    const laneH = 54;
    const cssH = lanes.length * laneH + 18; // +18 for the time axis
    const dpr = window.devicePixelRatio || 1;
    canvas.width = cssW * dpr;
    canvas.height = cssH * dpr;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    const samples = rec.samples;
    const padL = 46;
    const padR = 10;
    const plotW = cssW - padL - padR;
    const tMax = Math.max(1, (rec.endedAt || Date.now()) - rec.startedAt);
    const xOf = (t) => padL + (t / tMax) * plotW;

    const font = "10px 'Barlow', sans-serif";

    // Event markers first (vertical lines behind the traces)
    rec.events.forEach((e) => {
      const x = xOf(e.t);
      ctx.strokeStyle = (EVENT_COLOR[e.kind] || '#888') + '66';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, lanes.length * laneH);
      ctx.stroke();
    });

    lanes.forEach((lane, i) => {
      const top = i * laneH;
      const plotTop = top + 14;
      const plotBot = top + laneH - 8;

      // collect values across this lane's keys
      let min = Infinity;
      let max = -Infinity;
      samples.forEach((s) => lane.keys.forEach((k) => {
        const v = s[k];
        if (typeof v === 'number') { if (v < min) min = v; if (v > max) max = v; }
      }));
      if (!isFinite(min)) { min = 0; max = 1; }
      if (min === max) { min -= 1; max += 1; }
      const pad = (max - min) * 0.15 || 1;
      min -= pad; max += pad;
      const yOf = (v) => plotBot - ((v - min) / (max - min)) * (plotBot - plotTop);

      // lane separator + label
      ctx.strokeStyle = '#2a2f36';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(padL, plotBot); ctx.lineTo(cssW - padR, plotBot); ctx.stroke();
      ctx.fillStyle = lane.color;
      ctx.font = font;
      ctx.textAlign = 'left';
      ctx.fillText(lane.label, 4, top + 20);
      // min/max ticks
      ctx.fillStyle = '#6a7480';
      ctx.textAlign = 'right';
      ctx.fillText(String(Math.round(max)), padL - 4, plotTop + 4);
      ctx.fillText(String(Math.round(min)), padL - 4, plotBot);

      // traces
      lane.keys.forEach((k, ki) => {
        ctx.strokeStyle = lane.color;
        ctx.globalAlpha = ki === 0 ? 1 : 0.55; // diastolic dimmer
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        let started = false;
        samples.forEach((s) => {
          const v = s[k];
          if (typeof v !== 'number') return;
          const x = xOf(s.t); const y = yOf(v);
          if (!started) { ctx.moveTo(x, y); started = true; } else ctx.lineTo(x, y);
        });
        ctx.stroke();
        ctx.globalAlpha = 1;
      });
    });

    // time axis labels
    ctx.fillStyle = '#6a7480';
    ctx.font = font;
    ctx.textAlign = 'left';
    ctx.fillText('0:00', padL, lanes.length * laneH + 12);
    ctx.textAlign = 'right';
    ctx.fillText(mmss(tMax), cssW - padR, lanes.length * laneH + 12);
  }, [rec]);

  return <canvas ref={canvasRef} className={styles.chart} />;
}

// ── Case recorder panel ──────────────────────────────────────────────────────
export default function CaseRecorder() {
  const rec = useSimStore((s) => s.caseRecording);
  const startCase = useSimStore((s) => s.startCase);
  const endCase = useSimStore((s) => s.endCase);
  const resetCase = useSimStore((s) => s.resetCase);

  const [view, setView] = useState('trend'); // 'trend' | 'table'
  const [, tick] = useState(0);

  // Live elapsed clock while recording
  useEffect(() => {
    if (!rec.active) return;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [rec.active]);

  const hasRecording = rec.startedAt != null && rec.samples.length + rec.events.length > 0;
  const elapsed = rec.startedAt ? (rec.endedAt || Date.now()) - rec.startedAt : 0;
  const cols = activeCols(rec.samples);   // only channels that were actually recorded

  const doDownloadCSV = () => download(`case_${stamp(rec.startedAt)}.csv`, buildCSV(rec), 'text/csv');
  const doDownloadJSON = () =>
    download(`case_${stamp(rec.startedAt)}.json`, JSON.stringify({
      startedAt: rec.startedAt, endedAt: rec.endedAt, durationMs: elapsed,
      samples: rec.samples, events: rec.events,
    }, null, 2), 'application/json');

  // Hand the finished case to the /replay screen (opened in a new tab) via
  // localStorage (shared across tabs), so it can be scrubbed without re-uploading.
  const doReplay = () => {
    try {
      localStorage.setItem('sim.replayCase', JSON.stringify({
        startedAt: rec.startedAt, endedAt: rec.endedAt, durationMs: elapsed,
        samples: rec.samples, events: rec.events,
      }));
    } catch { /* quota/private mode — the /replay file loader still works */ }
    window.open('/replay', '_blank');
  };

  return (
    <section className={styles.recorder}>
      <div className={styles.bar}>
        <span className={styles.title}>Case Recording<HelpTip label="About case recording">Start a case to log vitals every 5 seconds plus every scenario, drug and shock you give. End the case to replay it on a timeline for debriefing, or download it as CSV/JSON.</HelpTip></span>

        {rec.active ? (
          <>
            <span className={styles.recDot}>● REC</span>
            <span className={styles.status}>{mmss(elapsed)} · {rec.samples.length} samples · {rec.events.length} events</span>
            <button className={`${styles.btn} ${styles.end}`} onClick={endCase}>■ End Case</button>
          </>
        ) : hasRecording ? (
          <>
            <span className={styles.status}>Ended · {mmss(elapsed)} · {rec.samples.length} samples · {rec.events.length} events</span>
            <button className={styles.btn} onClick={doReplay}>▷ Replay</button>
            <button className={styles.btn} onClick={doDownloadCSV}>⤓ CSV</button>
            <button className={styles.btn} onClick={doDownloadJSON}>⤓ JSON</button>
            <button className={styles.btn} onClick={startCase}>● New Case</button>
            <button className={styles.btnGhost} onClick={resetCase}>Clear</button>
          </>
        ) : (
          <button className={`${styles.btn} ${styles.start}`} onClick={startCase}>● Start Case</button>
        )}
      </div>

      {hasRecording && (
        <>
          <div className={styles.viewToggle}>
            <button className={view === 'trend' ? styles.tabOn : styles.tab} onClick={() => setView('trend')}>Trends</button>
            <button className={view === 'table' ? styles.tabOn : styles.tab} onClick={() => setView('table')}>Table</button>
          </div>

          {view === 'trend' ? (
            <div className={styles.trendWrap}>
              {rec.samples.length >= 2
                ? <TrendChart rec={rec} />
                : <div className={styles.empty}>Collecting samples… (one every 5 s)</div>}
              <div className={styles.legend}>
                {Object.entries(EVENT_COLOR).map(([k, c]) => (
                  <span key={k} className={styles.legendItem}><span className={styles.swatch} style={{ background: c }} />{k}</span>
                ))}
              </div>
            </div>
          ) : (
            <div className={styles.tables}>
              <div className={styles.tableBlock}>
                <div className={styles.tableHead}>Events</div>
                <div className={styles.scroll}>
                  <table className={styles.table}>
                    <thead><tr><th>Elapsed</th><th>Clock</th><th>Type</th><th>Detail</th></tr></thead>
                    <tbody>
                      {rec.events.length === 0 && <tr><td colSpan={4} className={styles.dim}>No events yet</td></tr>}
                      {rec.events.map((e, i) => (
                        <tr key={i}>
                          <td className={styles.mono}>{mmss(e.t)}</td>
                          <td className={styles.mono}>{e.at ? new Date(e.at).toLocaleTimeString([], { hour12: false }) : '—'}</td>
                          <td style={{ color: EVENT_COLOR[e.kind] || '#aaa' }}>{e.kind}</td>
                          <td>{e.label}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className={styles.tableBlock}>
                <div className={styles.tableHead}>Vitals ({rec.samples.length})</div>
                <div className={styles.scroll}>
                  <table className={styles.table}>
                    <thead><tr><th>Time</th>{cols.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                    <tbody>
                      {rec.samples.map((s, i) => (
                        <tr key={i}>
                          <td className={styles.mono}>{mmss(s.t)}</td>
                          {cols.map((c) => <td key={c} className={styles.mono}>{s[c] ?? ''}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
